import { twMerge } from "tailwind-merge";
import { ActionPanelContainer } from "../../actionPanel/ActionPanelContainer";
import {
  OrderFulfillmentType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import { capitalize } from "@sellar-io/common";
import dayjs from "dayjs";
import { useState } from "react";
import { useOrderIntegrationValueList } from "../../../hooks/api/v2/order/useOrderIntegrationValueList";
import { useOrderUpdate as useOrderUpdateV2 } from "../../../hooks/api/v2/order/v2/useOrderUpdate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { OrderShippingLabelModal } from "../../../pages/order/modals/OrderShippingLabelModal";
import {
  ChangeDeliveryDetailsDialog,
  ChangeDeliveryDetailsType,
} from "../../../pages/order/orderOnBehalfOf/dialogs/ChangeDeliveryDetailsDialog";
import { ActionPanel } from "../../actionPanel/ActionPanel";
import { SupplierOnly } from "../../authFilter/SupplierOnly";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  Icon,
} from "@sellar-io/components";
import { OrderStage } from "../helpers";
import { OrderAddress } from "../OrderAddress";
import { OrderDeliveryDate } from "../OrderDeliveryDate";
import { ApcTrackingDialog } from "./ApcTrackingDialog";
import { BuyerOnly } from "../../authFilter/BuyerOnly";
import { SellarDistributionSupplierCutOffAlertContainer } from "~/pages/order/Alerts/SellarDistributionSupplierCutOffAlert";

export type OrderSummaryPriceProps = {
  fulfillmentType: OrderGetResponseType["fulfillmentType"];
  DeliveryAddress: OrderGetResponseType["DeliveryAddress"];
  stage: OrderStage;
  deliveryDate?: Date;
  dispatchDate?: Date;
  distributionTimePreference?: "am" | "pm" | "allDay" | null;
  supplierName: string;
  buyerName: string;
  deliveryNotes: string | null;
  orderId: number;
  items: OrderGetResponseType["OrderItems"];
  supplierId: number;
  buyerPostcode: string;
  confirmedAt: Date | null;
  cancelledAt: Date | null;
  drayDeliveryContactDetails: string | null;
  paymentType: OrderGetResponseType["paymentType"];
  initiator: OrderGetResponseType["initiator"];
  SupplierPalletShipment: OrderGetResponseType["SupplierPalletShipment"];
  supplierUsesPalletInboundForOrder: boolean;
  readOnly?: boolean;
  supplierCutOffDate?: Date | null;
  supplierFinalisationCutOffAt: Date | null;
};

export const OrderDeliverySummary = (
  props: OrderSummaryPriceProps,
): React.ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const [shippingLabelActive, setShippingLabelActive] = useState(false);
  const [
    changeDeliveryDetailsModalActive,
    setChangeDeliveryDetailsModalActive,
  ] = useState<boolean>(false);
  const [showApcTracking, setShowApcTracking] = useState(false);

  const orderIntegrationValueQuery = useOrderIntegrationValueList(
    {
      orderId: props.orderId,
    },
    {
      enabled:
        (props.fulfillmentType === "ups" || props.fulfillmentType === "apc") &&
        !!props.orderId,
    },
  );

  const updateOrderMutationV2 = useOrderUpdateV2(props.orderId);

  const onChangeDeliveryDetailsSubmit = async (
    data: ChangeDeliveryDetailsType,
  ) => {
    if (!data) return;
    if (data.fulfillmentType === "dxBasic") {
      throw new Error("Cannot update an order with fulfillment type dxBasic");
    }
    const fulfillmentType =
      data.fulfillmentType === ("sellar-shipping" as OrderFulfillmentType)
        ? "courier"
        : data.fulfillmentType;

    await updateOrderMutationV2
      .mutateAsync({
        fulfillmentType,
        deliveryNotes: data.deliveryNotes ?? undefined,
        DeliveryAddress: data.deliveryAddress,
        requestingBusinessType: "supplier",
        fulfillmentChangeReason: data.fulfillmentChangeReason ?? undefined,
        fulfillmentChangeReasonMessage:
          data.fulfillmentChangeReasonMessage.trim() || undefined,
      })
      .then(() => {
        setChangeDeliveryDetailsModalActive(false);
      });
  };

  const getTitle = () => {
    if (!props.fulfillmentType) return "Delivery";

    if (props.fulfillmentType === "ups") return "Delivery: UPS";

    if (props.fulfillmentType === "apc") return "Delivery: APC";

    if (props.fulfillmentType === "sellarDistribution")
      return business?.Retailer
        ? "Delivery: Sellar"
        : "Delivery: Sellar Distribution";

    if (props.fulfillmentType === "dx" || props.fulfillmentType === "dxDropOff")
      return business?.Retailer ? "Delivery: Courier" : "Delivery: DX";

    return `Delivery: ${capitalize(props.fulfillmentType)}`;
  };

  const hideEditButton =
    !!orderIntegrationValueQuery.data?.pages[0]?.length &&
    props.fulfillmentType === "apc";

  // pallet shipment dates

  const palletDispatchDate = props.SupplierPalletShipment
    ? (props.SupplierPalletShipment.dispatchedAt ??
      props.SupplierPalletShipment.dispatchPlannedAt ??
      props.SupplierPalletShipment.expectedFinaliseDate)
    : null;

  const palletDeliveryDate = props.SupplierPalletShipment
    ? (props.SupplierPalletShipment.deliveredAt ??
      props.SupplierPalletShipment.deliveryPlannedAt ??
      props.SupplierPalletShipment.expectedArrivalDate)
    : null;

  const terminalDateClassName =
    props.stage === "Cancelled" || props.stage === "Voided"
      ? "text-zinc-500 line-through"
      : undefined;

  return (
    <>
      <Card className="bg-white">
        <CardHeader className={twMerge("bg-white", "p-4")}>
          <div className={"flex items-center justify-between gap-2"}>
            <div className="mr-auto flex flex-col">
              <div className="flex items-center gap-2">
                <H5>{getTitle()}</H5>
                <SellarDistributionSupplierCutOffAlertContainer
                  order={{
                    id: props.orderId,
                    confirmedAt: props.confirmedAt,
                    cancelledAt: props.cancelledAt,
                    fulfillmentType: props.fulfillmentType,
                    supplierFinalisationCutOffAt:
                      props.supplierFinalisationCutOffAt,
                  }}
                  mode="badge"
                />
              </div>
              {!props.confirmedAt && business?.Supplier && (
                <H6>
                  You can edit the delivery address before finalising this order
                </H6>
              )}
            </div>
            {!!orderIntegrationValueQuery.data?.pages[0]?.length &&
              props.confirmedAt &&
              business?.Supplier &&
              (props.fulfillmentType === "ups" ||
                props.fulfillmentType === "apc") && (
                <Button onClick={() => setShippingLabelActive(true)}>
                  <Icon size={0.7} icon={"DOWNLOAD"} />
                  Get labels
                </Button>
              )}
            {(props.stage === "Accepted" || props.stage === "Confirmed") &&
              !hideEditButton &&
              !props.readOnly && (
                <SupplierOnly>
                  <Button
                    onClick={() => setChangeDeliveryDetailsModalActive(true)}
                  >
                    <Icon size={0.7} icon={"PENCIL"} />
                    Edit
                  </Button>
                </SupplierOnly>
              )}
          </div>
        </CardHeader>
        <CardContent className={"p-4"}>
          <div className="flex flex-col gap-2">
            {!!orderIntegrationValueQuery.data?.pages[0]?.[0]?.value &&
              props.fulfillmentType === "ups" && (
                <div className={"flex flex-col gap-2"}>
                  <ActionPanel
                    title={"Track your order"}
                    message={`Get tracking information for each of your packages.`}
                  >
                    <Button
                      className={"mt-2 w-full md:w-min"}
                      onClick={() =>
                        window.open(
                          `https://www.ups.com/track?tracknum=${orderIntegrationValueQuery.data.pages[0]?.[0]?.value}`,
                          "_blank",
                        )
                      }
                    >
                      <Icon size={0.7} icon={"OPEN_IN_NEW"} />
                      Track delivery
                    </Button>
                  </ActionPanel>
                </div>
              )}

            {!!orderIntegrationValueQuery.data?.pages[0]?.[0]?.value &&
              props.fulfillmentType === "apc" && (
                <>
                  <div className={"flex flex-col gap-2"}>
                    <ActionPanel
                      title="Track your order"
                      message="View tracking information from APC for this order."
                    >
                      <Button
                        className={"mt-2 w-full md:w-min"}
                        onClick={() => setShowApcTracking(true)}
                      >
                        <Icon size={0.7} icon={"OPEN_IN_NEW"} />
                        View tracking info
                      </Button>
                    </ActionPanel>
                  </div>
                  <ApcTrackingDialog
                    active={showApcTracking}
                    setActive={setShowApcTracking}
                    orderId={props.orderId}
                  />
                </>
              )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {props.fulfillmentType === "collection" ? (
                <div className={"flex flex-col gap-2"}>
                  <H5>Delivery method</H5>
                  <H6>Collection</H6>
                </div>
              ) : props.fulfillmentType === "sellarDistribution" ? (
                <>
                  <div className={"flex flex-col gap-2"}>
                    <H5>Delivery method</H5>
                    <SupplierOnly>
                      <H6>Sellar Distribution</H6>
                    </SupplierOnly>
                    <BuyerOnly>
                      <H6>Sellar</H6>
                    </BuyerOnly>
                    <H5 className="mt-2">Delivery contact</H5>
                    <H6>{props.drayDeliveryContactDetails || "TBC"}</H6>

                    <div className="flex flex-col gap-2">
                      <H5>Customer delivery date</H5>
                      <H6 className={terminalDateClassName}>
                        {props.deliveryDate
                          ? dayjs(props.deliveryDate).format("DD/MM/YY")
                          : "TBC"}
                      </H6>
                    </div>
                  </div>
                  <SupplierOnly>
                    {props.supplierUsesPalletInboundForOrder && (
                      <div className="flex flex-col gap-2">
                        <H5>Pallet destination</H5>
                        <H6>
                          {props.SupplierPalletShipment?.DistributionWarehouse
                            ?.StockLocation.name ?? "TBC"}
                        </H6>
                        <H5 className="mt-2">Pallet dispatch date</H5>
                        <H6 className={terminalDateClassName}>
                          {palletDispatchDate
                            ? dayjs(palletDispatchDate).format("DD/MM/YY")
                            : "TBC"}
                        </H6>
                        <H5 className="mt-2">Pallet delivery date</H5>
                        <H6 className={terminalDateClassName}>
                          {palletDeliveryDate
                            ? dayjs(palletDeliveryDate).format("DD/MM/YY")
                            : "TBC"}
                        </H6>
                      </div>
                    )}
                  </SupplierOnly>
                </>
              ) : (
                <OrderAddress
                  address={props.DeliveryAddress}
                  title={"Address"}
                />
              )}
              {!props.supplierUsesPalletInboundForOrder && (
                <OrderDeliveryDate
                  fulfillmentType={props.fulfillmentType}
                  deliveryDate={props.deliveryDate}
                  dispatchDate={props.dispatchDate}
                  distributionTimePreference={props.distributionTimePreference}
                  stage={props.stage}
                  supplier={props.supplierName}
                  supplierId={props.supplierId}
                  buyerPostcode={props.buyerPostcode}
                  isSellarImportOrder={
                    props.paymentType === "cardPaymentWithDeposit"
                  }
                  supplierCutOffDate={props.supplierCutOffDate}
                />
              )}
            </div>
            {props.deliveryNotes && (
              <ActionPanelContainer
                type={"default"}
                className={"flex flex-col gap-2"}
              >
                <H5>Instructions</H5>
                <H6 className="break-words whitespace-pre-wrap">
                  {props.deliveryNotes}
                </H6>
              </ActionPanelContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {business?.Supplier?.id &&
        (props.fulfillmentType === "ups" ||
          props.fulfillmentType === "apc") && (
          <OrderShippingLabelModal
            active={shippingLabelActive}
            onDismiss={() => setShippingLabelActive(false)}
            orderId={props.orderId}
            buyerName={props.buyerName}
            fulfillmentType={props.fulfillmentType}
          />
        )}
      <ChangeDeliveryDetailsDialog
        key={props.orderId}
        address={props.DeliveryAddress}
        fulfillmentType={props.fulfillmentType}
        deliveryNotes={props.deliveryNotes}
        isFinalised={props.stage === "Confirmed"}
        active={changeDeliveryDetailsModalActive}
        onDismiss={() => {
          updateOrderMutationV2.reset();
          setChangeDeliveryDetailsModalActive(false);
        }}
        onSubmit={(data) => onChangeDeliveryDetailsSubmit(data)}
        error={updateOrderMutationV2.error}
        loading={updateOrderMutationV2.isPending}
        orderDate={props.deliveryDate}
        items={props.items}
        initiator={props.initiator}
        buyerName={props.buyerName}
      />
    </>
  );
};
